import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { Box } from "@mui/material";
import { MyHrContractCard, MyHrDataEditCollapse } from "my-hr/components";
import {
  useMyHrChangeRequests,
  useMyHrChangeRequestsExaminersName,
  useMyHrContract,
  useMyHrEmployee,
} from "my-hr/hooks";
import {
  OcCard,
  OcExaminersList,
  OcPageNameTitle,
  OcPageTransition,
} from "oc/components";
import {
  useOcButtonGroup,
  useOcCurrentUserState,
  useOcIsMobile,
} from "oc/hooks";

type ChangeRequestItem = {
  _id: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  type: string;
  createdAt: string;
  validFrom: string;
  oldValue: any;
  newValue: any;
  messages: any;
  typeGroup: string | null;
  typeGroupId: string | null;
  contract: string;
};

export const MyHrUserDataEditRequestsPage = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useOcIsMobile();
  const { employee } = useParams<{ employee?: string }>();
  const { employeeCode, contractCode } = useOcCurrentUserState();

  let param = employeeCode;
  if (employee) {
    param = employee;
  }
  const { data: employeeData } = useMyHrEmployee({
    employeeId: employee ?? "",
  });
  const { data: allData } = useMyHrChangeRequests(param);

  const [onlyRequested, setOnlyRequested] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [pagedData, setPagedData] = useState<ChangeRequestItem[]>([]);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const requestedData = allData?.filter(
    (item: ChangeRequestItem) =>
      item?.approvedAt === null && item?.rejectedAt === null,
  );

  let showData = requestedData;
  if (onlyRequested === false) {
    showData = allData;
  }
  const showDataTypeNull = showData?.filter(
    (item: ChangeRequestItem) => !item?.typeGroup,
  );
  const showDataTypeWithoutNull = showData?.filter(
    (item: ChangeRequestItem) => !!item?.typeGroup,
  );
  const groupsData = _.uniqBy(showDataTypeWithoutNull, "typeGroupId");
  const groupItems: ChangeRequestItem[] = groupsData.map(
    (group) =>
      showDataTypeWithoutNull.find(
        // @ts-ignore
        (item: ChangeRequestItem) => item.typeGroupId === group.typeGroupId,
      )!,
  );
  const allMergedData: ChangeRequestItem[] = groupItems.concat(
    showDataTypeNull as ChangeRequestItem[],
  );

  useEffect(() => {
    const newPagedData = (allMergedData ?? []).slice(0, pageSize);
    if (!_?.isEqual(pagedData, newPagedData)) {
      setPagedData(newPagedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMergedData, pageSize]);

  const handleFetchMore = () => {
    if (pagedData?.length >= allMergedData?.length) return;
    setPageSize((prev) => prev + 10);
  };

  useEffect(() => {
    const newPagedData = allMergedData?.slice(0, pageSize);
    if (!_?.isEqual(pagedData, newPagedData)) {
      setPagedData(newPagedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMergedData, pageSize]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleFetchMore();
        }
      },
      { threshold: 1.0 },
    );

    if (loadMoreRef?.current) {
      observer?.observe(loadMoreRef?.current);
    }

    return () => {
      if (loadMoreRef?.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer?.unobserve(loadMoreRef?.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagedData]);

  const buttonsDef = ["OC_TO_BE_ASSESSED", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MyHrUserDataEditRequests",
    buttonsDef,
  });
  let contract = contractCode;
  if (allData.length > 0) {
    contract = allData[0].contract;
  }

  const {
    data: examinersData,
    loading: examinersLoading,
    error: examinersError,
  } = useMyHrChangeRequestsExaminersName({
    locale: i18n.language,
    contract,
  });

  const { data: contractData } = useMyHrContract({
    contract,
  });

  const examinerRequestsDisabled = requestedData?.filter(
    (item: ChangeRequestItem) => item?.contract === contractCode,
  );

  let disabled = false;

  if (examinerRequestsDisabled?.length > 0) {
    disabled = true;
  }

  useEffect(() => {
    if (selectedButton === "OC_TO_BE_ASSESSED") {
      setOnlyRequested(true);
    } else if (selectedButton === "OC_ALL") {
      setOnlyRequested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedButton]);

  return (
    <OcPageTransition>
      <OcPageNameTitle
        title={
          employee
            ? t("MY_HR_USER_DATA_EDIT_PAGE_PAGE_TITLE")
            : t("MY_HR_MY_CHANGE_REQUEST")
        }
        goBack={employee ? true : false}
      />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        {employeeData && (
          <MyHrContractCard employee={employeeData} contract={contractData} />
        )}
        <OcCard
          label={employee ? t("OC_REQUESTS") : t("MY_HR_MY_CHANGE_REQUEST")}
        >
          <>
            <Box
              id="state-selector"
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding="25px 0px 0px 0px"
            >
              <OcButtonGroup />
            </Box>
            <Box height="32px" />
            {pagedData?.map((item, index) => (
              <Box key={`${item?._id}-${index}`}>
                <MyHrDataEditCollapse
                  employeeCode={param}
                  type={item.type}
                  createdDateAt={new Date(item.createdAt).toLocaleString()}
                  validDateFrom={new Date(item.validFrom).toLocaleDateString()}
                  oldValue={item.oldValue}
                  newValue={item.newValue}
                  messages={item.messages}
                  approvedAt={item.approvedAt}
                  rejectedAt={item.rejectedAt}
                  selectedId={item._id}
                  typeGroup={item.typeGroup ?? ""}
                  isAdmin={employee ? true : false}
                  typeGroupId={item.typeGroupId ?? ""}
                  disabled={disabled}
                />
                <Box height="16px" />
              </Box>
            ))}
            <Box ref={loadMoreRef} style={{ height: "10px" }} />
          </>
        </OcCard>
        <Box height="16px" />
        <Box width={isMobile ? "auto" : "240px"}>
          <OcCard
            label={t("OC_EXAMINERS")}
            loading={!examinersData && examinersLoading}
            error={!!examinersError}
          >
            <>
              <OcExaminersList
                examinersData={examinersData}
                loading={examinersLoading}
              />
            </>
          </OcCard>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyHrUserDataEditRequestsPage;
